import { useEffect, useState, useCallback } from "react";
import { Megaphone, X, AlertTriangle, Info, ShieldAlert } from "lucide-react";
import api from "../lib/api";

const SEVERITY = {
  info: { color: "#1FB6C1", icon: Info, label: "Info" },
  warning: { color: "#F59E0B", icon: AlertTriangle, label: "Warning" },
  critical: { color: "#F43F5E", icon: ShieldAlert, label: "Critical" },
};

/**
 * BulletinBanner — top-of-app broadcast banner. Polls /api/bulletins/active
 * every 5 minutes. Renders the most-recent non-dismissed bulletin first;
 * if multiple, lets the user cycle. Dismiss persists per-user on the server.
 *
 * Mounted in `Layout.jsx` for staff. Hidden when no active bulletins.
 */
export default function BulletinBanner() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/bulletins/active");
      setItems(Array.isArray(data) ? data : []);
      setActive(0);
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    const onRefresh = () => load();
    window.addEventListener("bulletins:refresh", onRefresh);
    return () => { clearInterval(id); window.removeEventListener("bulletins:refresh", onRefresh); };
  }, [load]);

  if (!items.length) return null;
  const b = items[active] || items[0];
  const meta = SEVERITY[b.severity] || SEVERITY.info;
  const Icon = meta.icon;

  const dismiss = async () => {
    try {
      await api.post(`/bulletins/${b.id}/dismiss`);
      const remaining = items.filter((x) => x.id !== b.id);
      setItems(remaining);
      setActive(0);
    } catch (_e) { /* silent */ }
  };

  return (
    <div
      className="w-full px-3 py-2 flex items-center gap-3 border-b text-xs"
      style={{
        borderColor: `${meta.color}55`,
        background: `linear-gradient(90deg, ${meta.color}1a, ${meta.color}0a)`,
        color: "#dde3ec",
      }}
      data-testid="bulletin-banner"
      data-bulletin-id={b.id}
    >
      <span
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md font-bold uppercase tracking-wider text-[10px] shrink-0"
        style={{ background: `${meta.color}26`, color: meta.color, border: `1px solid ${meta.color}55` }}
      >
        <Icon className="w-3 h-3" /> {meta.label}
      </span>
      <div className="flex-1 min-w-0 flex items-baseline gap-2 flex-wrap">
        <span className="font-bold truncate" data-testid="bulletin-title">{b.title}</span>
        {b.body && <span className="text-[#a0aabf] line-clamp-1" data-testid="bulletin-body">{b.body}</span>}
      </div>
      {items.length > 1 && (
        <div className="text-[10px] text-[#647087] shrink-0 flex items-center gap-1">
          <button
            type="button"
            onClick={() => setActive((i) => (i - 1 + items.length) % items.length)}
            className="hover:text-white px-1"
            data-testid="bulletin-prev"
          >‹</button>
          <span className="font-mono">{active + 1}/{items.length}</span>
          <button
            type="button"
            onClick={() => setActive((i) => (i + 1) % items.length)}
            className="hover:text-white px-1"
            data-testid="bulletin-next"
          >›</button>
        </div>
      )}
      <button
        type="button"
        onClick={dismiss}
        title="Dismiss"
        className="hv-btn hv-btn-ghost text-[10px] px-1.5 py-1 shrink-0"
        data-testid="bulletin-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

BulletinBanner.SeverityIcon = Megaphone; // re-export icon hook for any caller
